// Areena Bingo rules (decision 2026-09-29), shared by the server and the browser.
// 75 balls, 5×5 coupon (B 1–15, I 16–30, N 31–45, G 46–60, O 61–75) with a free centre square.
// Only horizontal rows count: stage 1 = one row, 2 = two rows … 5 = full coupon.
import { containsLink } from './chatRules.js';

export const BINGO_COLUMNS = ['B', 'I', 'N', 'G', 'O'] as const;
export const BINGO_MAX_NUMBER = 75;
export const BINGO_STAGES = 5;
/** A batch has at most this many coupons (decision 2026-09-29). */
export const BINGO_BATCH_MAX = 50;
export const BINGO_LABEL_MAX = 40;
export const BINGO_NICKNAME_MIN = 2;
export const BINGO_NICKNAME_MAX = 24;
/** The free centre square. */
export const FREE = 0;
/** Minimum time between two draws, so that the draw animation (≈3.5 s) can finish. */
export const BINGO_DRAW_INTERVAL_MS = 4_000;

/** 25 numbers in reading order (row by row), FREE in the centre. */
export type BingoCard = number[];

/** B 1–15, I 16–30 …: the column of a number (0–4). */
export const columnOf = (n: number) => Math.floor((n - 1) / 15);
/** "N 42" */
export const ballLabel = (n: number) => `${BINGO_COLUMNS[columnOf(n)]} ${n}`;

/**
 * A new random coupon. `rand(n)` returns an integer 0 ≤ x < n (crypto.randomInt on the server).
 * Each column has 5 different numbers from its range (the centre column 4 + the free square).
 */
export function generateCard(rand: (n: number) => number): BingoCard {
  const columns: number[][] = [];
  for (let c = 0; c < 5; c++) {
    const pool = Array.from({ length: 15 }, (_, i) => c * 15 + i + 1);
    const picked: number[] = [];
    for (let k = 0; k < 5; k++) picked.push(pool.splice(rand(pool.length), 1)[0]!);
    columns.push(picked);
  }
  const card: BingoCard = [];
  for (let r = 0; r < 5; r++) for (let c = 0; c < 5; c++) card.push(r === 2 && c === 2 ? FREE : columns[c]![r]!);
  return card;
}

export const cardKey = (card: BingoCard) => card.join(',');

/** For every row: how many of its numbers have not been drawn yet (the free square counts as drawn). */
export function rowMissing(card: BingoCard, drawn: ReadonlySet<number>): number[] {
  const missing: number[] = [];
  for (let r = 0; r < 5; r++) {
    let m = 0;
    for (let c = 0; c < 5; c++) {
      const n = card[r * 5 + c]!;
      if (n !== FREE && !drawn.has(n)) m++;
    }
    missing.push(m);
  }
  return missing;
}

/** Complete horizontal rows. */
export const completeRows = (card: BingoCard, drawn: ReadonlySet<number>) => rowMissing(card, drawn).filter((m) => m === 0).length;

/** How many more numbers the coupon needs for `rows` complete rows (0 = bingo at this stage). */
export function missingForStage(card: BingoCard, drawn: ReadonlySet<number>, rows: number): number {
  return rowMissing(card, drawn)
    .sort((a, b) => a - b)
    .slice(0, rows)
    .reduce((sum, m) => sum + m, 0);
}

/**
 * The numbers the coupon still needs for `rows` rows: those of its most complete rows (the admin's
 * view of coupons near a bingo, 2026-10-01).
 */
export function neededForStage(card: BingoCard, drawn: ReadonlySet<number>, rows: number): number[] {
  const missing = rowMissing(card, drawn);
  const best = missing
    .map((m, r) => ({ m, r }))
    .sort((a, b) => a.m - b.m || a.r - b.r)
    .slice(0, rows);
  const needed: number[] = [];
  for (const { r } of best) {
    for (let c = 0; c < 5; c++) {
      const n = card[r * 5 + c]!;
      if (n !== FREE && !drawn.has(n)) needed.push(n);
    }
  }
  return needed.sort((a, b) => a - b);
}

/**
 * Every number that can take the coupon towards the bingo of this stage the fastest (user request
 * 2026-10-02): the missing numbers of all rows that are among the most complete ones – e.g. five rows
 * one number short each in the first stage give five numbers, any of which is a bingo.
 */
export function bingoNumbers(card: BingoCard, drawn: ReadonlySet<number>, rows: number): number[] {
  const missing = rowMissing(card, drawn);
  const edge = [...missing].sort((a, b) => a - b)[Math.min(rows, 5) - 1]!;
  const numbers: number[] = [];
  missing.forEach((m, r) => {
    if (m === 0 || m > edge) return;
    for (let c = 0; c < 5; c++) {
      const n = card[r * 5 + c]!;
      if (n !== FREE && !drawn.has(n)) numbers.push(n);
    }
  });
  return numbers.sort((a, b) => a - b);
}

/**
 * After how many draws the coupon got its bingo for `rows` rows (0 = already before the first
 * draw), or null when it has no bingo. The automatic bingo compares these (old and new bingo).
 */
export function bingoAfter(card: BingoCard, draws: readonly number[], rows: number): number | null {
  const drawn = new Set<number>();
  if (missingForStage(card, drawn, rows) === 0) return 0;
  for (let i = 0; i < draws.length; i++) {
    drawn.add(draws[i]!);
    if (missingForStage(card, drawn, rows) === 0) return i + 1;
  }
  return null;
}

// --- Automatic bingo (decisions 2026-09-30, bingo-suunnitelma.md "Automaattinen bingo") ---------

/** After the first accepted Bingo! press the others have this long to press theirs. */
export const BINGO_CLAIM_WINDOW_MS = 10_000;
/** A drawn number is revealed (and read out) this long after the draw: the broadcast animation. */
export const BINGO_REVEAL_MS = 3_000;
export const BINGO_AUTO_INTERVAL_MIN = 5;
export const BINGO_AUTO_INTERVAL_MAX = 60;
/** Superjackpot: a full coupon by this number (decision 2026-10-01: fixed, not a setting – hard to win). */
export const BINGO_JACKPOT_DRAWS = 52;

export type BingoClaimVerdict = 'accepted' | 'not_bingo' | 'too_late';

/**
 * A Bingo! press. `at` = bingoAfter() of the coupon, `windowAt` = that of the first accepted press
 * of this stage (null while nobody has pressed). An old bingo can be called until someone calls a
 * newer one; after that only bingos at least as new as the first one share the win.
 */
export function judgeClaim(at: number | null, windowAt: number | null): BingoClaimVerdict {
  if (at === null) return 'not_bingo';
  if (windowAt !== null && at < windowAt) return 'too_late';
  return 'accepted';
}

/** The ad carousel (user request 2026-10-01). */
export interface BingoCarousel {
  /** In the carousel's order, at random (none repeated before all have played), or weighted at random. */
  order: 'sequence' | 'random' | 'weighted';
  /** Ads at the start of the game (before the first number) and at its end. */
  startCount: number;
  endCount: number;
  /** Ad breaks during the draw: every `breakEvery` numbers, `perBreak` ads. */
  breaks: boolean;
  breakEvery: number;
  perBreak: number;
  /** At most this many ads in a game, 0 = no limit. */
  maxPerGame: number;
}

/** Today in Finland as YYYY-MM-DD (an ad's validity period is in local days). */
export const finnishDate = (ms: number) => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Helsinki' }).format(ms);

/** Is the ad valid on the given day (YYYY-MM-DD)? */
export const adValidOn = (ad: { validFrom: string | null; validUntil: string | null }, day: string) =>
  (!ad.validFrom || ad.validFrom <= day) && (!ad.validUntil || day <= ad.validUntil);

/** Settings of the automatic bingo (Ylläpito → Bingo → Automaattinen). */
export interface BingoAutoConfig {
  /** Seconds between draws (at least the length of the number's sound). */
  intervalS: number;
  /** Registration closes this many minutes before the start. */
  closeBeforeMin: number;
  /** Fewer claimed coupons → the scheduled draw is cancelled. */
  minCoupons: number;
  /** At most this many coupons in the game; when full, registration closes. */
  maxCoupons: number;
  /** The scheduled game goes to the lobby announcement automatically, or the host announces it. */
  announce: 'auto' | 'manual';
  /** The start music after the "one minute" announcement. */
  music: boolean;
  /** Quiet background music during the game. */
  background: boolean;
  /** Waiting music from 30 minutes before the draw until the start (user request 2026-10-03). */
  waitMusic: boolean;
  /** The tracks in the library's order or at random (the same random order for everyone, 2026-10-03). */
  waitMusicOrder: 'list' | 'random';
  /**
   * The waiting music from the audio stream instead of the files (2026-10-03). The stream is never the
   * background music of the game (user decision 2026-10-07).
   */
  waitMusicStream: boolean;
  /** The rules read out when the coupon claiming has closed (2026-10-03, sound `ohjeet`). */
  instructions: boolean;
  /** Ads on or off (off: none plays, the carousel settings are kept; 2026-10-01). */
  ads: boolean;
  /** The ads in the carousel, in their order (the sequence order). */
  adIds: number[];
  /** How the ads are played (Mainoskaruselli, 2026-10-01). */
  carousel: BingoCarousel;
  /** Fillers (phrases, jokes) between the draws every `fillerEvery` numbers (2026-10-01). */
  fillers: boolean;
  fillerEvery: number;
  /** Superjackpot (2026-09-30): a full coupon by the 52nd number (`BINGO_JACKPOT_DRAWS`, fixed) wins it too. */
  jackpot: boolean;
  /** A scheduled game: the first number is drawn at `at`. */
  schedule: { at: number; batchIds: number[] } | null;
}

export const DEFAULT_AUTO_CONFIG: BingoAutoConfig = {
  intervalS: 10,
  closeBeforeMin: 5,
  minCoupons: 2,
  maxCoupons: 200,
  announce: 'auto',
  music: true,
  background: false,
  waitMusic: false,
  waitMusicOrder: 'list',
  waitMusicStream: false,
  instructions: false,
  ads: true,
  adIds: [],
  carousel: {
    order: 'sequence',
    startCount: 1,
    endCount: 1,
    breaks: false,
    breakEvery: 10,
    perBreak: 1,
    maxPerGame: 0,
  },
  fillers: false,
  fillerEvery: 8,
  jackpot: false,
  schedule: null,
};

/** The waiting music starts this long before the scheduled draw (user request 2026-10-03). */
export const BINGO_WAIT_MUSIC_MS = 30 * 60 * 1000;
export const BINGO_MUSIC_MAX_BYTES = 12 * 1024 * 1024;

/** A track of the waiting music (admin). */
export interface BingoMusicView {
  id: number;
  name: string;
  ms: number;
  bytes: number;
  /** Levels the tracks to each other (measured in the admin's browser). */
  volume: number;
}

/**
 * The waiting music of the scheduled automatic game: the tracks in their order from `from` until
 * `until` (the start announcements), looping; every page plays the same point.
 */
export interface BingoWaitMusic {
  from: number;
  until: number;
  /** In playing order (a random order is mixed by the server, the same for everyone). */
  tracks: { id: number; ms: number; volume: number }[];
  /** The audio stream instead of the tracks (2026-10-03). */
  stream: string | null;
}

/** The same "random" order for every page: shuffled with a seed (the scheduled time). */
export function seededOrder<T>(items: readonly T[], seed: number): T[] {
  let s = Math.floor(seed) % 2147483647 || 1;
  const next = () => (s = (s * 48271) % 2147483647) / 2147483647;
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/** Where the waiting music is at server time `now`: the track and the point in it (the list loops). */
export function waitPosition(plan: BingoWaitMusic, now: number): { index: number; offsetMs: number } | null {
  const total = plan.tracks.reduce((s, t) => s + t.ms, 0);
  if (total <= 0 || now < plan.from || now >= plan.until) return null;
  let t = (now - plan.from) % total;
  for (const [index, track] of plan.tracks.entries()) {
    if (t < track.ms) return { index, offsetMs: t };
    t -= track.ms;
  }
  return null;
}

/** A sound everyone plays at the same moment (server time `at`, ms). */
export interface BingoCue {
  id: number;
  at: number;
  /** Address of the sound. */
  src: string;
  /** Length (ms). */
  ms: number;
  kind: 'voice' | 'music' | 'ad';
  /** Playback volume 0–1 (ads are levelled to the speech). */
  volume: number;
  /** Sponsor image shown while the ad plays. */
  image: string | null;
}

export interface BingoAutoView {
  paused: boolean;
  /** The last draw is revealed (shown and read out) at this server time. */
  revealAt: number;
  /** Before the first draw: when it comes (the countdown on the pages, 2026-10-01). */
  firstAt: number | null;
  /** Bingo! presses being checked: the window closes at `endsAt`. */
  claims: { endsAt: number; cards: { cardNumber: number; nickname: string }[]; jackpot: boolean } | null;
  background: boolean;
  /** Superjackpot of the game: still possible (`open`), won, or gone; null = not in use. */
  jackpot: { draws: number; state: 'open' | 'won' | 'gone' } | null;
}

export interface BingoAdView {
  id: number;
  /** A sponsor ad, or a filler phrase / joke between the draws (2026-10-01). */
  kind: 'ad' | 'filler';
  name: string;
  ms: number;
  volume: number;
  hasImage: boolean;
  createdAt: number;
  /** The carousel (2026-10-01): weight 1–10 in the weighted order, validity period (YYYY-MM-DD, null = open). */
  weight: number;
  validFrom: string | null;
  validUntil: string | null;
  /** Times played (all time). */
  plays: number;
}

/** The sponsor report: plays of each ad in a period (the name is kept even if the ad is removed). */
export interface BingoAdPlayStat {
  adId: number | null;
  name: string;
  plays: number;
  first: number;
  last: number;
}

// --- Views ------------------------------------------------------------------------------------

export interface BingoBatchView {
  id: number;
  label: string;
  /** Coupons of the batch are numbered firstNumber … firstNumber + size − 1 (unique over batches). */
  firstNumber: number;
  size: number;
  registrationOpen: boolean;
  claimed: number;
  createdAt: number;
  /** The running game uses this batch. */
  inGame: boolean;
}

export interface BingoCodeView {
  code: string;
  /** The coupon number (unique over batches), null while the code is free. */
  cardNumber: number | null;
  nickname: string | null;
  claimedAt: number | null;
  /** Sent to someone by text message (not free any more, 2026-10-01). */
  sms: boolean;
  /** Reserved for a player (sign-up form, 2026-10-02) but not activated yet: `nickname` is the player's. */
  reserved: boolean;
}

/**
 * A code that belongs to a player (admin tab "Koodit ja pelaajat", user request 2026-10-02): reserved
 * for the player (no coupon yet) or claimed (a coupon).
 */
export interface BingoHeldCodeView {
  code: string;
  batchId: number;
  batchLabel: string;
  /** The coupon claiming of the batch is open. */
  claimingOpen: boolean;
  playerId: number;
  nickname: string;
  /** The coupon number, null while only reserved. */
  cardNumber: number | null;
  claimedAt: number | null;
  /** The player was last seen on the bingo page (ms), null = never. */
  lastSeenAt: number | null;
}

export interface BingoWinnerView {
  stage: number;
  cardNumber: number;
  nickname: string;
  afterDraws: number;
  /** Won the superjackpot too. */
  jackpot: boolean;
}

/** The next draw announced in the lobby and on the bingo page (user request 2026-09-30). */
export interface BingoNextDraw {
  /** Time of the draw (ms). */
  at: number;
  /** Optional short text, e.g. "Perjantairadion lähetyksessä". */
  text: string;
}

/** Public state of the game (number board, broadcast view). `version` changes on every change. */
export interface BingoPublicState {
  version: number;
  /** Server clock (ms) when the answer was made: the pages play the cues in sync by it. */
  now: number;
  /** Sponsor logo in the free square: its version (image /api/bingo/sponsor?v=…), null = none. */
  sponsor: number | null;
  next: BingoNextDraw | null;
  /** Some batch has its coupon claiming open (2026-10-01). */
  claiming: boolean;
  /** The sign-up form for codes by text message is open (2026-10-01). */
  signup: boolean;
  /** Sounds of the automatic bingo (the latest ones). */
  cues: BingoCue[];
  /** A scheduled automatic game: the first number at this time. */
  scheduledAt: number | null;
  /** The coupon claiming of the scheduled game closes at this time (the page shows the draw view, 2026-10-03). */
  closeAt: number | null;
  /** The waiting music before the scheduled game (2026-10-03). */
  waitMusic: BingoWaitMusic | null;
  /**
   * The background music of the game already from the start of the rules (read over it, user request
   * 2026-10-03); the game's own background music continues it.
   */
  waitBackground: { from: number } | null;
  auto: BingoAutoView | null;
  game: {
    id: number;
    /** Hosted by the administrator, or the automatic bingo. */
    mode: 'host' | 'auto';
    status: 'running' | 'finished';
    stage: number;
    /** Drawn numbers in order. */
    draws: number[];
    winners: BingoWinnerView[];
    /** Coupons in the game. */
    players: number;
    /** When the game ended (ms), null while it runs. */
    endedAt: number | null;
    /** Bingo! presses waiting for the host (hosted game, 2026-10-01). */
    calls: BingoCallView[];
  } | null;
}

export interface BingoTrackedCard {
  cardNumber: number;
  nickname: string;
  /** Numbers still needed for the current stage. */
  missing: number;
  /** The coupon (the admin sees the coupons near a bingo as pictures, 2026-10-01). */
  cells: BingoCard;
}

/** The host sees who is close to a win and who has a bingo (decision 2026-09-29). */
export interface BingoHostState extends BingoPublicState {
  bingo: BingoTrackedCard[];
  near: BingoTrackedCard[];
  /** Batches of the game (only the host sees them; players see no batch labels). */
  labels: string[];
  /** Time left before the next draw is allowed (ms). */
  nextDrawInMs: number;
}

/** A player's own coupon (fetched with the code); no batch label (user request 2026-09-30). */
export interface BingoCouponView {
  code: string;
  number: number;
  nickname: string;
  cells: BingoCard;
}

export const stageLabel = (stage: number) => (stage >= BINGO_STAGES ? 'Täysi kuponki' : stage === 1 ? '1 vaakarivi' : `${stage} vaakariviä`);

export function isValidCard(card: unknown): card is BingoCard {
  if (!Array.isArray(card) || card.length !== 25) return false;
  const seen = new Set<number>();
  for (let i = 0; i < 25; i++) {
    const n = card[i];
    if (i === 12) {
      if (n !== FREE) return false;
      continue;
    }
    if (typeof n !== 'number' || !Number.isInteger(n) || n < 1 || n > BINGO_MAX_NUMBER || seen.has(n)) return false;
    if (columnOf(n) !== i % 5) return false;
    seen.add(n);
  }
  return true;
}

/**
 * Superjackpot (decision 2026-09-30): the full coupon's bingo arose by the `limit`th number
 * (`at` = bingoAfter) and was called before the next number was drawn (`drawn` at the call).
 */
export const winsJackpot = (at: number, drawn: number, limit: number) => at <= limit && drawn <= limit;

/** A bingo player (decisions 2026-10-01): a unique nickname, coupons claimed with it. */
export interface BingoPlayerView {
  id: number;
  nickname: string;
  /** Own limit of coupons per game, null = the general limit. */
  maxCoupons: number | null;
  /** Coupons in all batches that still exist. */
  coupons: number;
  /** Coupons in batches not played yet or open for claiming (counted against the limit). */
  upcoming: number;
  /** Codes reserved for the player but not activated yet (sign-up form, 2026-10-02). */
  reserved: number;
  createdAt: number;
  /** Last seen on the bingo page (ms), null = never. */
  lastSeenAt: number | null;
}

/**
 * Presence (user decision 2026-10-02): when the coupon claiming of a batch closes, only the players
 * seen on the bingo page this recently keep their coupons; the others' codes are voided.
 */
export const BINGO_PRESENT_MS = 2 * 60 * 60 * 1000;
/** The bingo page of a logged-in player says it is open this often. */
export const BINGO_HEARTBEAT_MS = 60 * 1000;

// --- The bingo log (user request 2026-10-02) ----------------------------------------------------

/**
 * start / draw / stage / end; bingo = a coupon's bingo of the stage arose (detail 'carried': it was ready
 * when the stage began); old = a bingo not called before the next number; press = a Bingo! press (detail:
 * accepted, already, wait); wrong = no bingo; late = an old bingo after a newer one; win = confirmed winner.
 */
export type BingoLogKind = 'start' | 'draw' | 'bingo' | 'old' | 'press' | 'wrong' | 'late' | 'win' | 'stage' | 'end';

export interface BingoLogEvent {
  at: number;
  kind: BingoLogKind;
  stage: number;
  /** How many numbers had been drawn at the event (the coupon picture marks these). */
  drawSeq: number;
  number: number | null;
  cardNumber: number | null;
  nickname: string | null;
  cells: BingoCard | null;
  detail: string | null;
}

export interface BingoLogSummary {
  gameId: number;
  mode: 'host' | 'auto';
  labels: string;
  coupons: number;
  players: number;
  startedAt: number;
  endedAt: number | null;
  /** Numbers drawn. */
  draws: number;
  wins: number;
}

export interface BingoLogView extends Omit<BingoLogSummary, 'draws' | 'wins'> {
  draws: number[];
  events: BingoLogEvent[];
}

/** A Bingo! press in the hosted game: the host checks and confirms it (user request 2026-10-01). */
export interface BingoCallView {
  cardNumber: number;
  nickname: string;
  /** When it was pressed (ms). */
  at: number;
}

// --- Codes by text message (decisions 2026-10-01) ------------------------------------------------

/** At most this many codes in all per phone number or e-mail address (user decision 2026-10-02). */
export const SIGNUPS_PER_PHONE = 3;
/** One sign-up asks 1–3 codes (user decision 2026-10-02). */
export const SIGNUP_CODES_MAX = 3;
/** At most this many sign-ups from one network (IP) a day; reopening the form starts anew. */
export const SIGNUPS_PER_IP = 3;
export const EMAIL_MAX = 254;

/** A plausible e-mail address in lower case, or null. */
export function normalizeEmail(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  return s.length <= EMAIL_MAX && /^[^\s@<>(),;:"[\]]+@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(s) ? s : null;
}

/**
 * A phone number in the international form (+358401234567), or null. Finnish numbers may be written
 * with a leading 0; spaces, dashes and brackets are ignored.
 */
export function normalizePhone(raw: string): string | null {
  let s = raw.replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) s = `+${s.slice(2)}`;
  else if (s.startsWith('0')) s = `+358${s.slice(1)}`;
  else if (s.startsWith('358')) s = `+${s}`;
  return /^\+[1-9]\d{7,14}$/.test(s) ? s : null;
}

/** "+358401234567" → "040 123 4567" for Finnish numbers (the admin's list). */
export function formatPhone(e164: string): string {
  if (!e164.startsWith('+358')) return e164;
  const local = `0${e164.slice(4)}`;
  return `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`.trim();
}

/** The public state of the sign-up form. */
export interface BingoSignupPublic {
  open: boolean;
  /** The maximum of codes has been reached. */
  full: boolean;
  /** Extra text on the form (e.g. the time of the draw). */
  info: string;
  /** Cloudflare Turnstile site key (empty: bot check not set up → the form is closed). */
  siteKey: string;
  /** Codes can be asked by e-mail too (the mailbox is set up). */
  email: boolean;
}

/** The form settings (admin). */
export interface BingoSignupConfig {
  open: boolean;
  maxCodes: number;
  info: string;
  /** The text message; {koodi} (all the codes of the sign-up) and {nimi} (the nickname) are replaced. No links (2026-10-02). */
  template: string;
  /**
   * Sending (user request 2026-10-03): text messages and e-mails each at once (from the instant batch) or
   * by hand (the host's "Lähetä koodit"). No instant batch → by hand.
   */
  smsInstant: boolean;
  emailInstant: boolean;
  /** The batch the codes sent at once come from (before 2026-10-03: emailBatchId, e-mail only). */
  instantBatchId: number | null;
  emailSubject: string;
  /** The e-mail; {nimi} (the nickname) and {koodi} are replaced. No links (2026-10-02). */
  emailTemplate: string;
  /**
   * Sign-up by text message "BINGO 3 Nickname" (user decisions 2026-10-07): off → the phone's inbox is not
   * read and nothing is sent. Messages are taken only while the form is open.
   */
  smsSignup: boolean;
  /** The IP lock (user request 2026-10-07): from one network (IP) only one nickname while the form is open. */
  ipLock: boolean;
}

export const DEFAULT_SIGNUP_CONFIG: BingoSignupConfig = {
  open: false,
  maxCodes: 50,
  info: '',
  // No ä/ö in the text message: they would cut one message to 70 characters (UCS-2).
  template: 'Areena Bingo: bingokoodisi {koodi}. Hae kupongit osoitteessa areena.eu/bingo ennen arvontaa.',
  smsInstant: false,
  emailInstant: false,
  instantBatchId: null,
  emailSubject: 'Areena Bingo – bingokoodisi',
  // The same texts are written by migration 031.
  emailTemplate:
    'Hei {nimi}!\n\nAreena Bingon bingokoodisi: {koodi}\n\nSyötä jokin koodeistasi bingosivulla areena.eu/bingo – kaikki kuponkisi tulevat silloin näkyviin. ' +
    'Tule sivulle arvontaa edeltävien 2 tunnin aikana, ennen kuin kuponkien haku sulkeutuu: vain paikalla olevien kupongit ovat mukana arvonnassa.' +
    '\n\nTervetuloa pelaamaan!\nAreena Bingo',
  smsSignup: false,
  ipLock: false,
};

/** The fixed replies to sign-ups by text message (user decision 2026-10-07; at most 70 characters = one message). */
export const SMS_SIGNUP_REPLIES = {
  format: 'Areena Bingo: viestin muoto on BINGO 3 Nimimerkki (1–3 koodia).',
  nickname: 'Areena Bingo: nimimerkissä 2–24 merkkiä, ei linkkejä.',
  nickname_taken: 'Areena Bingo: nimimerkki on varattu, valitse toinen.',
  phone_limit: 'Areena Bingo: numerolla on jo enimmäismäärä koodeja (3).',
  closed: 'Areena Bingo: ilmoittautuminen on nyt suljettu.',
  full: 'Areena Bingo: bingo on täynnä, ilmoittautuminen on suljettu.',
  received: 'Areena Bingo: ilmoittautuminen saatu, koodit tulevat myöhemmin.',
} as const;
/** Error replies per number in 24 hours; after that the number's wrong messages get no reply. */
export const SMS_SIGNUP_ERROR_REPLIES = 3;

export type SmsSignupParse = { ok: true; coupons: number; nickname: string } | { ok: false; reason: 'not_bingo' | 'format' | 'nickname' };

/**
 * A sign-up text message: "BINGO <1–3> <nickname>" – the space after the number may be left out, any case,
 * without the number 3 codes (user decision 2026-10-07). Not starting with "bingo" → not a sign-up.
 */
export function parseSmsSignup(raw: string): SmsSignupParse {
  const text = raw.replace(/\s+/g, ' ').trim();
  if (!/^bingo/i.test(text)) return { ok: false, reason: 'not_bingo' };
  const m = /^bingo(?: ?(\d+))? (\S.*)$/i.exec(text);
  if (!m) return { ok: false, reason: 'format' };
  const coupons = m[1] === undefined ? SIGNUP_CODES_MAX : Number(m[1]);
  if (coupons < 1 || coupons > SIGNUP_CODES_MAX) return { ok: false, reason: 'format' };
  const nickname = m[2]!;
  if (nickname.length < BINGO_NICKNAME_MIN || nickname.length > BINGO_NICKNAME_MAX || containsLink(nickname)) return { ok: false, reason: 'nickname' };
  return { ok: true, coupons, nickname };
}

/**
 * A message from a template (text message or e-mail): {koodi} = the codes of the sign-up ("ABCD 2345,
 * EFGH 6789"), {nimi} = the nickname. No links (user decision 2026-10-02).
 */
export function signupMessage(template: string, codes: readonly string[], nickname: string): string {
  const list = codes.map((c) => `${c.slice(0, 4)} ${c.slice(4)}`).join(', ');
  return template.replaceAll('{koodi}', list).replaceAll('{nimi}', nickname);
}

/** The phone (SMS Gateway for Android, local server) – the password is never sent back. */
export interface BingoSmsConfig {
  url: string;
  username: string;
  hasPassword: boolean;
}

/** The mailbox the codes are sent from (SMTP over TLS) – the password is never sent back. */
export interface BingoMailConfig {
  host: string;
  port: number;
  username: string;
  hasPassword: boolean;
}

export type BingoSignupStatus = 'pending' | 'sending' | 'sent' | 'delivered' | 'failed';

export interface BingoSignupView {
  id: number;
  /** The nickname (2026-10-02; before that the name). */
  name: string;
  /** Codes asked (1–3). */
  coupons: number;
  /** Either the phone number or the e-mail address is set. */
  phone: string | null;
  email: string | null;
  createdAt: number;
  status: BingoSignupStatus;
  batchLabel: string | null;
  /** The codes given (reserved for the nickname), none before sending. */
  codes: string[];
  error: string | null;
  sentAt: number | null;
  /** From the form or by text message (2026-10-07). */
  via: 'form' | 'sms';
}
